import React, {Fragment, useRef} from 'react';

import cs from 'classnames';
import {browserHistory, Link} from 'react-router';

import {DocumentActionsButton} from 'app/components/general/DocumentActionsButton';
import ObjectSaveStatus from 'app/components/general/ObjectSaveStatus';
import StateButton from 'app/general/widgets/StateButton';
import RequestOrApproveButton from 'app/procedures/components/page-headers/RequestOrApproveButton';
import {gt} from 'app/utils/i18n';
import {getLocation} from 'app/utils/urls';

import type {ActionableDocumentType, Ref} from 'app/general/types';
import type {ReactNode} from 'react';
import type {DocumentContentType} from 'sweet-types';

type DocumentVersionType = {
  id: number | string;
  created_at: string;
  approved_at: string | null;
  is_current: boolean;
  url: string;
};
type CommonProps = {
  additionalContent?: ReactNode | ((node: ReactNode) => ReactNode);
  disableApproval?: boolean;
  document: ActionableDocumentType;
  contentType: DocumentContentType;
  canChange: boolean;
  isBusy?: boolean;
  isDropDown?: boolean;
  refreshData?: () => void;
};
type ViewPublishedModeProps = CommonProps & {
  pageContext: 'view-published';
};
type ViewVersionModeProps = CommonProps & {
  pageContext: 'view-version';
  isLastVersion: boolean;
  version: DocumentVersionType;
  onApprovalRequest?: (
    requestApproveFunction: () => Promise<unknown>
  ) => Promise<unknown>;
  onApprove?: (approveFunction: () => Promise<unknown>) => Promise<unknown>;
};
type EditModeProps = CommonProps & {
  pageContext: 'edit';
  version: DocumentVersionType;
  updateVersion: () => Promise<any>;
  status: 'editing' | 'saved' | 'saving' | 'error';
  saveAndView: () => void;
  onApprovalRequest?: (
    requestApproveFunction: () => Promise<unknown>
  ) => Promise<unknown>;
  onApprove?: (approveFunction: () => Promise<unknown>) => Promise<unknown>;
};
type Props = EditModeProps | ViewPublishedModeProps | ViewVersionModeProps;
export const GenericDocumentActionButtons = (props: Props) => {
  const {
    canChange,
    document,
    contentType,
    additionalContent,
    isBusy,
    disableApproval,
    isDropDown = true,
  } = props;
  const actionButtonsRefs = useRef<HTMLDivElement | null>(null);
  const nextReviewDate =
    'next_review_at' in document ? document?.next_review_at : null;
  const managers = 'managers' in document ? document?.managers : [];

  const renderOrCall = (
    element: ReactNode | ((node: Ref<ReactNode>) => ReactNode)
  ) => {
    return typeof element === 'function' ? element(actionButtonsRefs) : element;
  };

  const currVersionTooltipTextSelector = (document: ActionableDocumentType) =>
    'approved_at' in document && document?.approved_at
      ? gt('Live')
      : gt('Most Recent Draft');

  const goToCurrentVersion = () => {
    const currLocation = browserHistory.getCurrentLocation();
    const {document} = props;
    const location = getLocation(document.html_url + currLocation.search);
    browserHistory.replace(location.pathname + location.search);
  };
  const onSaveAndView = () => {
    if (props.pageContext === 'edit') {
      return props.saveAndView();
    }
  };

  const renderEditButton = (className: string) => {
    return (
      <Link
        className={cs('btn ml-1 btn-sm tour-edit', className)}
        to={`${props.document.html_url}edit/`}
      >
        <i className="fa fa-fw fa-edit" />
        <span className="d-none d-lg-inline"> {gt('Edit')}</span>
      </Link>
    );
  };

  return (
    <div
      ref={actionButtonsRefs}
      className="d-flex flex-no-wrap align-items-center  d-print-none"
    >
      {props.pageContext === 'edit' ? (
        <ObjectSaveStatus
          updateVersion={props.updateVersion}
          lastModified={props.version.created_at}
          status={props.status}
        />
      ) : null}

      {additionalContent != null ? renderOrCall(additionalContent) : null}

      {props.pageContext === 'view-published' ||
      props.pageContext === 'edit' ? (
        <DocumentActionsButton
          pageContext={props.pageContext}
          refreshData={props.refreshData}
          document={document}
        >
          <button className="btn btn-sm btn-light procedure-actions-tour">
            <i className="fa fa-fw fa-bolt" />
            <span className="d-none d-lg-inline"> {gt('Actions')}</span>
          </button>
        </DocumentActionsButton>
      ) : null}

      {props.pageContext === 'view-version' ? (
        <Fragment>
          <button
            className="btn btn-default btn-light mr-1 btn-sm"
            onClick={goToCurrentVersion}
          >
            <span className="d-none d-md-inline">
              {gt('View %s', currVersionTooltipTextSelector(document))}
            </span>
            <span className="d-md-none">
              {currVersionTooltipTextSelector(document)}
            </span>
          </button>
          {props.pageContext === 'view-version' &&
          props.isLastVersion &&
          canChange
            ? renderEditButton('btn-light mr-2')
            : null}
        </Fragment>
      ) : null}

      {props.pageContext === 'edit' ? (
        <StateButton
          className="btn btn-light mx-1 view-draft-tour btn-sm"
          disabled={props.isBusy || false}
          onClick={onSaveAndView}
          busyText={gt('View')}
        >
          <i className="fa fa-fw fa-caret-right" />
          <span className="d-none d-md-inline">{gt('View Version')}</span>
          <span className="d-md-none">{gt('View')}</span>
        </StateButton>
      ) : null}

      {canChange ? (
        props.pageContext === 'view-published' ? (
          renderEditButton('btn-primary')
        ) : props.pageContext === 'edit' ||
          props.pageContext === 'view-version' ? (
          <RequestOrApproveButton
            versionURL={props.version.url}
            documentId={document.id}
            versionId={props.version.id}
            documentHtmlURL={document.html_url}
            contentType={contentType}
            className="btn btn-primary request-or-approve-tour btn-sm"
            disabled={isBusy == true || disableApproval == true || false}
            canApprove={document.can_edit}
            versionApprovedAt={props.version.approved_at}
            versionIsCurrent={props.version.is_current}
            managers={managers}
            onApprovalRequest={props.onApprovalRequest}
            onApprove={props.onApprove}
            refreshData={props.refreshData}
            nextReviewDate={nextReviewDate}
            isDropDown={isDropDown}
          />
        ) : null
      ) : null}
    </div>
  );
};
